/* Coordinate existing helpers without changing consent or app configuration. */
(() => {
  'use strict';
  let scheduled = false;
  let consentRoot;
  let consentButton;
  let buyButton;
  let buyChoice;
  // One entry per observed target: a callback batch only reports the targets
  // that changed, so the page state is the OR over everything still in view.
  const buyInView = new Map();
  // Page-state classes. They replace html:has()/body:has() rules whose arguments
  // (open, hidden, data-visible) change constantly: Chrome re-checked those rules on
  // every such change and restyled the whole page each time (~35 s of style work
  // after a homepage load in the 2026-09-26 trace). A class flips only when the
  // state itself changes.
  const STATE_CLASSES = [
    ['body', 'dv-state-sticky-atc', '.dv-sticky-atc[data-visible="true"]'],
    ['body', 'dv-state-sticky-cart', '.dv-sticky-cart[data-visible="true"]'],
    ['body', 'dv-state-compare-dock', '.dv-compare-dock:not([hidden])'],
    ['body', 'dv-state-compare-tray', '.dv140-compare-tray:not([hidden])'],
    ['body', 'dv-state-menu-open', '#Details-menu-drawer-container[open]'],
    ['body', 'dv-state-overlay-open', 'dialog[open],details[open] > .search-modal,.mobile-facets__disclosure[open]'],
    ['body', 'dv-state-facets-open', '.mobile-facets__disclosure[open]'],
    ['html', 'dv-state-compare-dialog', '.dv-compare-dialog[open]'],
    ['html', 'dv-state-product-modal', 'product-modal[open]']
  ];
  const syncStates = () => {
    for (const [scope, name, selector] of STATE_CLASSES) {
      const target = scope === 'html' ? document.documentElement : document.body;
      target.classList.toggle(name, Boolean(document.querySelector(selector)));
    }
  };
  const buyObserver = new IntersectionObserver(entries => {
    for (const entry of entries) buyInView.set(entry.target, entry.isIntersecting);
    const visible = [...buyInView.values()].some(Boolean);
    document.body.classList.toggle('dv-conversion-buy-visible', visible);
    schedule();
  });
  const sync = () => {
    scheduled = false;
    const dock = document.querySelector('.dv-compare-dock');
    const slot = document.querySelector('[data-dv-conversion-compare]');
    if (dock && slot && dock.parentElement !== slot) slot.append(dock);
    const currentButton = document.querySelector('.product-form__submit,.cart__checkout-button');
    const currentChoice = document.querySelector('.dv-buy-mode');
    if (currentButton !== buyButton || currentChoice !== buyChoice) {
      buyObserver.disconnect();
      buyInView.clear();
      buyButton = currentButton;
      buyChoice = currentChoice;
      for (const target of [buyButton, buyChoice]) if (target) buyObserver.observe(target);
      if (!buyButton && !buyChoice) document.body.classList.remove('dv-conversion-buy-visible');
    }

    const cmp = document.querySelector('pandectes-cmp');
    if (cmp?.shadowRoot && consentRoot !== cmp.shadowRoot) {
      consentRoot = cmp.shadowRoot;
      const style = document.createElement('style');
      style.textContent = '.widget.bottom { bottom:calc(var(--float,16px) + var(--dv-purchase-clearance,0px)) !important; display:var(--dv-consent-widget-display,flex) !important; }';
      consentRoot.append(style);
      new MutationObserver(schedule).observe(consentRoot, { childList:true, subtree:true, attributes:true, attributeFilter:['class','open','hidden','aria-hidden'] });
    }
    const widget = consentRoot?.querySelector('button.widget');
    // Only the storefront footer is a persistent consent entry point.
    // Product dialogs also contain footers and precede it in document order.
    const footer = document.querySelector('footer.footer');
    if (widget && footer && !consentButton?.isConnected) {
      consentButton = document.createElement('button');
      consentButton.type = 'button';
      consentButton.className = 'dv-consent-settings';
      consentButton.textContent = 'Stillingar á vafrakökum';
      consentButton.setAttribute('aria-haspopup', 'dialog');
      consentButton.addEventListener('click', () => consentRoot?.querySelector('button.widget')?.click());
      (footer.querySelector('.footer__content-bottom-wrapper') || footer).append(consentButton);
    }
    const mobileConsentLink = Boolean(widget && consentButton?.isConnected && matchMedia('(max-width:749px)').matches);
    if (consentButton && consentButton.hidden === mobileConsentLink) consentButton.hidden = !mobileConsentLink;
    const widgetDisplay = mobileConsentLink ? 'none' : 'flex';
    if (cmp && cmp.style.getPropertyValue('--dv-consent-widget-display') !== widgetDisplay) cmp.style.setProperty('--dv-consent-widget-display', widgetDisplay);
    const sticky = document.querySelector('.dv-sticky-cart[data-visible="true"],.dv-sticky-atc[data-visible="true"]');
    const clearance = sticky && matchMedia('(max-width:749px)').matches ? `${Math.ceil(sticky.getBoundingClientRect().height) + 12}px` : '0px';
    if (cmp && cmp.style.getPropertyValue('--dv-purchase-clearance') !== clearance) cmp.style.setProperty('--dv-purchase-clearance',clearance);
    // A fixed purchase bar otherwise covers the footer shortcut at the end of the page
    // (the theme preview lifts the bar 64px; device safe areas make it taller).
    const footerClearance = mobileConsentLink && sticky ? `${Math.ceil(sticky.offsetHeight + (parseFloat(getComputedStyle(sticky).bottom) || 0)) + 12}px` : '';
    if (consentButton && consentButton.style.marginBottom !== footerClearance) consentButton.style.marginBottom = footerClearance;
    const consentDialog = consentRoot?.querySelector('[role="dialog"],dialog[open]');
    const open = Boolean(consentDialog?.getClientRects().length);
    document.body.classList.toggle('dv-conversion-consent-open', open);
    syncStates();
  };
  function schedule() {
    if (!scheduled) { scheduled = true; requestAnimationFrame(sync); }
  }
  function start() {
    sync();
    new MutationObserver(schedule).observe(document.body, { childList:true, subtree:true, attributes:true, attributeFilter:['data-visible','hidden','open'] });
    window.addEventListener('resize',schedule,{passive:true});
    document.addEventListener('shopify:section:load',schedule);
    document.addEventListener('click',event => {
      const link = event.target.closest('a.dv-conversion-pack,.dv-conversion-shortcuts a,.dv-empty-state a');
      if (!link || !window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) return;
      // No search terms, visitor identifiers or external requests are added.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({event:'dv_conversion_navigation',release:'20260917',surface:link.classList.contains('dv-conversion-pack') ? 'pack' : link.closest('.dv-conversion-shortcuts') ? 'menu' : 'recovery',destination:link.getAttribute('href')});
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true});
  else start();
})();
